<!--
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
-->
<template>
  <div
    class="landing-page"
    :class="{
      'search-contact': showSearchResult,
      'popup': forgotPasswordPopup,
      'connect': onSearchScreen,
      'show-help': onQRHelp,
      'signin': onSignIn,
      'search-domain': showSearchDomain,
    }"
  >
    <div class="side-1" />
    <div class="side-2">
      <!-- Popup window for the pages start -->
      <div
        v-if="forgotPasswordPopup"
        class="popup-container"
        @click="hideForgotPassword"
      >
        <div
          class="card easy-card-white reset-password"
          @click.stop
        >
          <form-button
            class="back"
            :text="$t('APP_TEXT_BACK')"
            icon="SvgArrowDown"
            link
            @onClick="hideForgotPassword"
          />
          <form
            class="card-body"
            @submit.prevent="resetPassword"
          >
            <div class="row mb-3">
              <div class="col">
                <h5>{{ $t('APP_TEXT_RESET_PASSWORD') }}</h5>
                <h6>{{ $t('APP_TEXT_RESET_PASSWORD_INFO') }}</h6>
              </div>
            </div>
            <div class="row justify-content-center text-left">
              <form-input
                ref="forgotEmailInput"
                v-model="forgotEmail"
                type="email"
                :label="{text:$t('APP_TEXT_EMAIL'),icon:'SvgEmail'}"
                righticon="SvgCloseStroke"
                :placeholder="$t('APP_TEXT_EMAIL_PLACEHOLDER')"
                @rightIconClick="clearForgotEmail"
              />
            </div>
            <div class="row justify-content-center mt-4">
              <div class="col">
                <form-button
                  v-if="requestingReset"
                  type="primary"
                  class="float-right"
                  icon="SvgLoading"
                  :text="$t('APP_TEXT_RESET')"
                />
                <form-button
                  v-else
                  type="primary"
                  btn-type="submit"
                  class="float-right"
                  :text="$t('APP_TEXT_RESET')"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <!-- Header section start -->
      <div class="header">
        <div class="row top-nav">
          <div class="col-4 v-align btn-container">
            <form-button
              class="back"
              :text="$t('APP_TEXT_BACK')"
              icon="SvgArrowDown"
              link
              @onClick="onGoBack"
            />
          </div>
          <div class="col-4 v-align center logo-container">
            <img
              :src="$_LOGOS.BRAND_LOGO_DEFAULT_LIGHT"
              alt
            >
          </div>
          <div class="col-4 v-align search-container">
            <form-input
              ref="contactSearchInputField"
              v-model="searchTerm"
              class="contact-search"
              type="text"
              lefticon="SvgSearch"
              righticon="SvgCloseStroke"
              :righticononfocusonly="false"
              :placeholder="$t('APP_TEXT_FREECLIENT_SEARCH_PLACEHOLDER')"
              :class="{'on-focus': showSearchResult}"
              @rightIconClick="clearSearch"
            />
            <form-input
              v-if="$_CONFIG.appconfig.ALLOW_TO_CHANGE_DOMAIN_IN_CLIENT"
              ref="domainSearchInputField"
              v-model="domainSearchInput"
              class="domain-search"
              type="text"
              lefticon="SvgSearch"
              righticon="SvgCloseStroke"
              :righticononfocusonly="false"
              :placeholder="$t('APP_TEXT_FREECLIENT_SEARCH_DOMAIN_PLACEHOLDER')"
              :class="{'on-focus': showSearchDomain}"
              @rightIconClick="clearDomainSearch"
            />
          </div>
        </div>
        <div class="search-content-box">
          <div class="col h-100 w-100 p-0">
            <div class="card easy-card-white m-0 h-100 p-0">
              <div
                ref="landingSearchResults"
                class="card-body user-list custom-scroll"
                :class="{empty: searchResults.length <= 0}"
              >
                <template v-if="dialContact || searchResults.length > 0">
                  <form-button
                    type="primary"
                    class="nav-up"
                    icon="SvgUpNav"
                    rounded
                    @onClick="scrollUP"
                  />
                  <form-button
                    type="primary"
                    class="nav-down"
                    icon="SvgDownNav"
                    rounded
                    @onClick="scrollDOWN"
                  />
                  <ContactCard
                    v-if="dialContact"
                    :contact="dialContact"
                    :is-dial="true"
                    :is-logged-in="false"
                    @call="initializeCall(dialContact)"
                  />
                  <ContactCard
                    v-for="search in searchResults"
                    :key="search.id"
                    :contact="search"
                    :is-search="true"
                    :is-logged-in="false"
                    @call="initializeCall(search)"
                    @cardClick="showContactInfo(search)"
                  />
                </template>
                <div
                  v-else
                  class="empty-box"
                >
                  <svg-icon icon="SvgContact" />
                  <h4>{{ $t('APP_TEXT_NO_SEARCH_RESULTS') }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="$_CONFIG.appconfig.ALLOW_TO_CHANGE_DOMAIN_IN_CLIENT && showSearchDomain"
          class="search-domain-box"
        >
          <div class="col h-100 w-100 p-0">
            <div class="card easy-card-white m-0 h-100 p-0">
              <div class="card-body domain-list custom-scroll">
                <div
                  v-if="filteredDomains.length <= 0"
                  class="empty-box"
                >
                  <svg-icon icon="SvgOrg" />
                  <h4>{{ $t('APP_TEXT_NO_ORGANIZATION_FOUND') }}</h4>
                </div>
                <template v-else>
                  <div
                    v-for="domain in filteredDomains"
                    :key="`landing_domain_${domain.domainid}`"
                    class="row domain-list-item"
                    :class="{ 'no-img': !domain.hasImage}"
                    @click="gotoDomain(domain)"
                  >
                    <div class="col-auto pr-0">
                      <div class="org-pic">
                        <img
                          v-if="domain.hasImage"
                          v-imageloded="domain"
                          :src="domain.BRAND_LOGO_ICON"
                        >
                        <svg-icon
                          v-else
                          icon="SvgPhoto"
                        />
                      </div>
                    </div>
                    <div class="col org-name">
                      {{ domain.domainname }}
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Content section start -->
      <div class="search-message">
        <div class="text">
          {{ $t('APP_TEXT_FREECLIENT_SEARCH_INFO_LABEL') }}
        </div>
      </div>
      <!-- Search result start -->
      <div
        v-if="!showSearchResult"
        class="content row"
      >
        <div class="col empty-container" />
        <div class="col btn-container">
          <form-button
            v-if="$_CONFIG.appconfig.ALLOW_ANONYMOUS_CONNECT"
            :text="$t('APP_TEXT_CONNECT')"
            icon="SvgCallCircle"
            class="btn-primary connect"
            @onClick="showSearchScreen"
          />
          <form-button
            :text="$t('APP_TEXT_SIGNIN')"
            icon="SvgUser"
            class="btn-primary"
            @onClick="showSignIn"
          />
        </div>
        <div
          v-if="onSignIn"
          class="col signin-container p-0"
        >
          <div class="card easy-card-white">
            <div class="card-body">
              <!-- Back Button -->
              <form-button
                v-if="isDesktopApp"
                :text="$t('APP_TEXT_CANCEL')"
                class="back"
                icon="SvgArrowDown"
                link
                @onClick="onGoBack"
              />
              <!-- Login Box -->
              <form
                class="login-box"
                :class="{'oauth': isOauthEnabled, invalid: loginFailed}"
                action="#"
                @submit.prevent="login"
              >
                <div class="row">
                  <div class="col-12">
                    <div class="row">
                      <div class="col-12">
                        <h4 class="heading">
                          {{ $t('APP_TEXT_SIGN_IN') }}
                        </h4>
                      </div>
                    </div>
                    <div
                      v-if="$_CONFIG.appconfig.ALLOW_TO_CHANGE_DOMAIN_IN_CLIENT"
                      class="row org-container"
                    >
                      <div
                        class="col-12"
                      >
                        <h5>
                          <b>{{ $t('APP_TEXT_ORGANIZATION') }}</b> - {{ $t('APP_TEXT_SELECT_DOMAIN_LABEL') }}
                        </h5>
                        <form-input
                          :value="$_CONFIG.appconfig.is_default_domain ? null : $_CONFIG.appconfig.DOMAIN_NAME"
                          class="select-org"
                          :fake-input="true"
                          :righticon="$_CONFIG.appconfig.is_default_domain ? 'SvgArrowRight' : 'SvgCloseStroke'"
                          :right-icon-always="true"
                          :placeholder="$t('APP_TEXT_DOMAIN_ADDRESS_PLACEHOLDER')"
                          @rightIconClick="onResetDomain"
                          @click="onShowDomainSearch"
                        />
                      </div>
                    </div>
                    <template v-if="isOauthEnabled">
                      <form-button
                        v-if="isLoggingIn"
                        type="primary"
                        class="btn-oauth"
                        icon="SvgLoading"
                        :text="$_CONFIG.appconfig.OAUTH_CONFIG.ENABLE_LOGIN_BUTTON_IMAGE ? '' : signInWithText"
                      />
                      <template v-else>
                        <img
                          v-if="$_CONFIG.appconfig.OAUTH_CONFIG.ENABLE_LOGIN_BUTTON_IMAGE"
                          class="btn-oauth-img"
                          :src="$_CONFIG.appconfig.OAUTH_CONFIG.LOGIN_BUTTON_IMAGE"
                          @click="loginWith"
                        >
                        <form-button
                          v-else
                          type="primary"
                          class="btn-oauth"
                          :text="signInWithText"
                          :image-src="$_CONFIG.appconfig.OAUTH_CONFIG.LOGIN_BUTTON_ICON ? $_CONFIG.appconfig.OAUTH_CONFIG.LOGIN_BUTTON_ICON : null"
                          @onClick="loginWith"
                        />
                      </template>
                    </template>
                    <template v-else>
                      <div class="row signin-form">
                        <div
                          class="col-12"
                        >
                          <h5>
                            <b>{{ $t('APP_TEXT_SIGN_IN_CREDENTIALS') }}</b>
                          </h5>
                          <form-input
                            ref="usernameInput"
                            v-model="username"
                            type="text"
                            lefticon="SvgUser"
                            righticon="SvgCloseStroke"
                            :placeholder="$t('APP_TEXT_EMAIL')"
                            @rightIconClick="clearUsername"
                          />
                          <form-input
                            v-model="password"
                            class="mb-2"
                            type="password"
                            lefticon="SvgLock"
                            righticon="SvgCloseStroke"
                            :placeholder="$t('APP_TEXT_PASSWORD')"
                            @rightIconClick="clearPassword"
                          />
                        </div>
                      </div>
                      <div class="invalid-container">
                        <h3>
                          <svg-icon icon="SvgCloseStroke" />
                          {{ $t('APP_TEXT_INVALID_CREDENTIALS') }}
                        </h3>
                        <h5>
                          {{ $t('APP_TEXT_INVALID_CREDENTIALS_INFO') }}
                        </h5>
                      </div>
                      <div class="row mt-1 remember-forgot">
                        <div class="col remember-me pr-0">
                          <FormCheckbox
                            v-model="rememberMe"
                            data-qa="remember_me_checkbox"
                          />
                          <label class="text-primary">&nbsp;{{ $t('APP_TEXT_REMEMBER_ME') }}</label>
                        </div>

                        <div
                          v-if="!$_CONFIG.appconfig.EXTERNAL_AUTH"
                          class="col forgot pl-0"
                        >
                          <div class="form-group mb-0">
                            <form-button
                              :text="$t('APP_TEXT_FORGOT_PASSWORD')"
                              link
                              @onClick="showForgotPassword"
                            />
                          </div>
                        </div>

                        <div class="col try-again pl-0">
                          <form-button
                            class="btn-secondary"
                            :text="$t('APP_TEXT_TRY_AGAIN')"
                            @onClick="trySigninAgan"
                          />
                        </div>
                      </div>
                      <div class="row mt-2 mb-2 lgn-btn-container">
                        <div class="col signup">
                          <div
                            v-if="!$_CONFIG.appconfig.EXTERNAL_AUTH && $_CONFIG.appconfig.ALLOW_SIGNUP"
                            class="form-group"
                          >
                            <form-button
                              link
                              :text="$t('APP_TEXT_SIGNUP')"
                              @onClick="gotoSignUp"
                            />
                          </div>
                        </div>
                        <div class="col pl-0">
                          <div class="form-group text-right signin-btn">
                            <form-button
                              v-if="isLoggingIn"
                              type="primary"
                              icon="SvgLoading"
                            />
                            <form-button
                              v-else
                              class="btn-secondary"
                              btn-type="submit"
                              :text="$t('APP_TEXT_SIGNIN')"
                            />
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                  <MaskedLoginForm ref="maskedLoginForm" />
                </div>
              </form>
            </div>
          </div>
        </div>
        <div class="col qr-container">
          <form-button
            v-show="$_CONFIG.appconfig.is_default_domain"
            :icon="onQRHelp ? 'SvgArrowDown' : 'SvgHelp'"
            class="help d-none"
            rounded
            @onClick="showQRHelp"
          />
          <div
            v-show="$_CONFIG.appconfig.is_default_domain"
            class="qr-box d-none"
          >
            <div class="qr-details">
              <h4>{{ $t('APP_TEXT_QRCODE_HEADING') }}</h4>
              <ol>
                <li>{{ $t('APP_TEXT_QRCODE_STEP_1') }}</li>
                <li>{{ $t('APP_TEXT_QRCODE_STEP_2') }}</li>
                <li>{{ $t('APP_TEXT_QRCODE_STEP_3') }}</li>
              </ol>
            </div>
            <div class="qr-code-container">
              <QrCode @gotAuthToken="onQRGotToken" />
            </div>
          </div>
        </div>
      </div>
      <!-- Footer section start -->
      <div
        v-if="!showSearchResult"
        class="footer row"
      >
        <div class="col">
          <form-button
            :text="$t('APP_TEXT_SETTINGS')"
            class="btn-back d-none"
            icon="SvgSettings"
            link
            @onClick="showPairingScreen"
          />
        </div>
        <div
          v-if="!isDesktopApp"
          class="col text-center terms"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="$t('APP_TEXT_TERMS_STATEMENT_LANDING')" />
          <a @click="openTermsOfService">{{ $t('APP_TEXT_TERMS_OF_SERVICE') }}</a>
        </div>
        <div class="col text-right language-container">
          <div
            v-if="languages"
            class="btn btn-link"
          >
            <svg-icon icon="SvgGlobe" />
            {{ language }}
            <select
              v-model="language"
              @change="languageChange"
            >
              <option
                v-for="(option,index) in languages"
                :key="`language_land_${index}`"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import landing from '@/app/landing';
import QrCode from '@/components/QrCode';
import MaskedLoginForm from '@/components/controls/MaskedLoginForm';
import ContactCard from '@/components/ContactCard';
export default {
  name: 'Landing',
  components: {
    QrCode,
    MaskedLoginForm,
    ContactCard
  },
  extends: landing
};
</script>

<style scoped>
</style>
